import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateTireDamageList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/tire-damage/search');
};

export const useTireDamageDataList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue, excludeIds = []) => {
  const isFetchAll = page === undefined;

  const companies = (selectedCompanies || []).map((c) => c.value).join(',');
  const start = isFetchAll ? 0 : page * rowsPerPage;

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const fb = new FilterBuilder().setPaging(start, rowsPerPage).addJoin('company', ['name']).setOrder(['id'], false);

    if (searchKey && searchValue) {
      if (searchKey === 'all') {
        fb.like('code_internal', searchValue, 'OR');
        fb.like('name', searchValue, 'OR');
        fb.like('area', searchValue, 'OR');
        fb.like('cause', searchValue, 'OR');
        fb.like('recommend_action', searchValue, 'OR');
        fb.like('description', searchValue, 'OR');
      } else {
        fb.like(searchKey, searchValue, 'AND');
      }
    }

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    if (excludeIds && excludeIds.length > 0) {
      fb.where('not in', 'id', excludeIds.join(','));
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companies, isFetchAll, excludeIds]);

  const key = useMemo(() => ['/tire-damage/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  return {
    ...normalizeApiResponse(data, error, isLoading),
    refetch: () => mutate(key)
  };
};

export const getTireDamageList = async () => {
  try {
    const fb = new FilterBuilder().setPaging(0, 9999).addJoin('company', ['name']).setOrder(['id'], false).filterByCompany();

    const payload = fb.build();
    const response = await fleetApi.post(`/tire-damage/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? [];
    return Array.isArray(dataArr) ? dataArr : [];
  } catch (error) {
    console.error('Failed to fetch tire damages:', error);
    return [];
  }
};

export const getTireDamageById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.get(`/tire-damage/${id}${getTzParam()}`);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);
    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : dataArr || null;
    return result;
  } catch (error) {
    console.error('Failed to fetch tire damage by ID:', error);
    throw error;
  }
};

export const createTireDamage = async (formData = {}) => {
  try {
    const body = { ...formData };
    const response = await fleetApi.post(`/tire-damage/new${getTzParam()}`, body);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireDamageList();
    return response;
  } catch (error) {
    console.error('Error creating tire damage:', error);
    throw error;
  }
};

export const updateTireDamage = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const response = await fleetApi.put(`/tire-damage/edit/${id}${getTzParam()}`, body);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireDamageList();
    return response;
  } catch (error) {
    console.error('Error updating tire damage:', error);
    throw error;
  }
};

export const deleteTireDamage = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/tire-damage/delete/${id}${getTzParam()}`);
    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateTireDamageList();
    return response;
  } catch (error) {
    console.error('Error deleting tire damage:', error);
    throw error;
  }
};
